/**
 * Hartes LEX→JUD-Join + Auto-Gate (ohne lexProof-Import — Node-strip-types).
 * Produktvertrag 26.08.2026: norm_refs ∩ lexCarry.norm_ids, Auto nur Fall-Rechtsfrage.
 * 27.08.2026 fail-closed: „dazu“ nur wenn Headnote Gesetz+Zahl der LEX-ID trägt;
 * unsicher → Empty. Auto nicht bei Pin + Floskel ohne eigene Rechtsfrage.
 * Pin-Prefix zählt nicht als klare Rechtsfrage (Klasse JUD_AUTO_JOIN).
 * Klasse JUD_JOIN_FRAGEPUNKT: gleiche LEX-ID reicht nicht — Headnote muss zum
 * gestellten Punkt (z. B. Schrift/Befristung vs. Untergang Abs 1 Z 2).
 */

export const PORTAL_LEX_JUD_JOIN_MARKER = 'portal-lex-jud-join:hard-auto-v1';
/** Dist-Fingerprint: Auto-JUD ohne Fake-User-Turn (Chip-Klick bleibt sichtbar). */
export const PORTAL_LEX_JUD_JOIN_NO_GHOST_MARKER = 'portal-lex-jud-join:no-ghost-user-v1';
/** Dist-Fingerprint: eine ARKO-Stimme + ausklappbare LEX/JUD-Originale, kein zweiter Wortlaut-Turn. */
export const PORTAL_LEX_JUD_JOIN_VOICE_MARKER = 'portal-lex-jud-join:one-voice-v1';
/** Dist-Fingerprint: Themenfilter nach hartem Join (Hitchhiker nicht in der Synthese). */
export const PORTAL_LEX_JUD_JOIN_TOPIC_MARKER = 'portal-lex-jud-join:topic-filter-v1';
/** Dist-Fingerprint: Headnote muss Gesetz+Zahl der LEX-ID tragen, sonst Empty. */
export const PORTAL_LEX_JUD_JOIN_HEADNOTE_MARKER = 'portal-lex-jud-join:headnote-fail-closed-v1';
/** Dist-Fingerprint: Join nur zum gestellten §-Fragepunkt, nicht nur gleiche LEX-ID. */
export const PORTAL_LEX_JUD_JOIN_FRAGEPUNKT_MARKER = 'portal-lex-jud-join:fragepunkt-v1';
export const PORTAL_JUD_SEARCH_LAWYER_MARKER = 'portal-jud-search:lawyer-search-v1';
export const PORTAL_JUD_SEARCH_FIELD_MARKER = 'portal-jud-search:own-field-v1';

export const JOIN_SYNTHESIS_CITE_MAX = 2;

export const JUD_HARD_JOIN_EMPTY_TEXT =
  'Im aktuellen Judikatur-Bestand liegt kein OGH-Fall zu diesen Normen vor. Es wird nichts ergänzt.';

export const JUD_TOPIC_JOIN_EMPTY_TEXT =
  'Im aktuellen Judikatur-Bestand liegt kein OGH-Fall zur gestellten Rechtsfrage vor. Es wird nichts ergänzt.';

const JOIN_GZ_RE = /\b\d+\s+[A-Za-zÄÖÜäöü]{1,8}\s+\d+\/\d+[a-zA-Z]?\b/g;

export type JoinLexSource = {
  norm_id?: string;
  title?: string;
  fragment_text?: string;
};

export type JoinJudCase = {
  geschaeftszahl?: string;
  date?: string;
  leitsatz?: string;
  kernsaetze?: string[];
  rechtssaetze?: string[];
  norm_refs?: string[];
  score?: number;
};

export type JoinJudProof = {
  status?: string;
  cases?: JoinJudCase[];
};

export type JoinLexProof = {
  sources?: JoinLexSource[];
};

/** Wortlaut-Block nur als Lead beim Composer-§, nicht bei der Fall-Join-Stimme. */
export function shouldRenderLexQuoteAsLead(joinVoice?: boolean): boolean {
  return !joinVoice;
}

function stripJoinStamps(text: string): string {
  return String(text || '')
    .replace(/\n*\[LEX:[^\]]+\]\s*$/i, '')
    .replace(/\n*\[JUD:[^\]]+\]/gi, '')
    .replace(/^Hier der verifizierte Wortlaut:\s*\n+/i, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** GZ intern = UI: Whitespace normalisieren, Suffix-Buchstabe nicht verdrehen (18i ≠ 18l). */
export function formatJoinGeschaeftszahl(raw: string): string {
  return String(raw || '').replace(/\s+/g, ' ').trim();
}

function assignedGeschaeftszahlen(proof?: JoinJudProof | null): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of proof?.cases || []) {
    const gz = formatJoinGeschaeftszahl(item.geschaeftszahl || '');
    if (!gz || seen.has(gz)) continue;
    seen.add(gz);
    out.push(gz);
  }
  return out;
}

function dropUnassignedGeschaeftszahlen(text: string, allowed: string[]): string {
  const allow = new Set(allowed.map((gz) => formatJoinGeschaeftszahl(gz)));
  return text.replace(JOIN_GZ_RE, (hit) => {
    const gz = hit.replace(/\s+/g, ' ').trim();
    return allow.has(gz) ? hit : '';
  }).replace(/[ \t]{2,}/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

function lexFallbackLine(lexProof?: JoinLexProof | null): string {
  const sources = (lexProof?.sources || []).filter((s) => String(s.norm_id || '').trim());
  if (!sources.length) return '';
  const labels = sources.slice(0, 3).map((s) => {
    const id = String(s.norm_id || '').trim();
    const title = String(s.title || '').replace(/\s+/g, ' ').trim();
    return title ? `${id} (${title})` : id;
  });
  return `Die belegte Norm ${labels.join(', ')} liegt im Normenbestand vor. Der Originalwortlaut steht unten.`;
}

function oghSentence(item: JoinJudCase): string {
  const gz = formatJoinGeschaeftszahl(item.geschaeftszahl || '');
  if (!gz) return '';
  const when = String(item.date || '').trim();
  const quote = String(item.leitsatz || item.kernsaetze?.[0] || item.rechtssaetze?.[0] || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 420);
  const dateBit = when ? ` vom ${when}` : '';
  if (quote) return `Der OGH hat dazu in ${gz}${dateBit} festgehalten: „${quote}“`;
  return `Der OGH hat dazu in ${gz}${dateBit} entschieden.`;
}

/**
 * Kurze ARKO-Stimme nur aus den zwei themenpassenden Quellen.
 * Kein zweiter fertiger Wortlaut-/OGH-Block, keine extra GZ, kein fragment_text im Lead.
 */
export function buildLexJudJoinSynthesis(input: {
  lexEinordnung: string;
  lexProof?: JoinLexProof | null;
  judProof?: JoinJudProof | null;
  userQuestion?: string;
}): string {
  const einordnung = stripJoinStamps(input.lexEinordnung);
  const lexLine = einordnung || lexFallbackLine(input.lexProof);
  const rawCases = input.judProof?.status === 'ok' ? (input.judProof.cases || []) : [];
  if (rawCases.length === 0) {
    const mergedEmpty = [lexLine, JUD_HARD_JOIN_EMPTY_TEXT].filter(Boolean).join('\n\n');
    return dropUnassignedGeschaeftszahlen(mergedEmpty, []);
  }
  const filtered = filterCasesByJoinTopic(rawCases, {
    userQuestion: input.userQuestion,
    lexSources: input.lexProof?.sources,
  });
  const cite = filtered.synthesis;
  const oghLine = cite.length
    ? cite.map(oghSentence).filter(Boolean).join(' ')
    : JUD_TOPIC_JOIN_EMPTY_TEXT;
  const allowedGz = assignedGeschaeftszahlen({ cases: cite });
  const merged = [lexLine, oghLine].filter(Boolean).join('\n\n');
  return dropUnassignedGeschaeftszahlen(merged, allowedGz);
}

export const OGH_FOLLOWUP_ACTION = 'ogh_dazu' as const;

export type OghFollowupOrigin = 'auto' | 'chip';

/**
 * Auto = intern, Merge in dieselbe ARKO-Blase (eine Stimme + Klappen).
 * Chip-Klick = echte User-Aktion, Blase darf den Chip-Text zeigen.
 */
export function shouldShowOghFollowupUserBubble(origin: OghFollowupOrigin): boolean {
  return origin === 'chip';
}

/** Composer-§-Nachschlagen (Wortlaut), nicht Fall-Rechtsfrage. */
export function isComposerParagraphLookup(text: string): boolean {
  const t = String(text || '').replace(/\s+/g, ' ');
  return /Bitte nur nachschlagen:/i.test(t) && /keine Fallanalyse/i.test(t);
}

export function isPremiumPortalCaller(extraBody: Record<string, unknown> | undefined | null): boolean {
  if (!extraBody || typeof extraBody !== 'object') return false;
  if (extraBody.isPremium === true) return true;
  return String(extraBody.tier || '').trim().toLowerCase() === 'premium';
}

/** Kanon `LAW-NUM` — `MRG-29`, `§ 29 MRG`, `LFG-24f`. */
export function canonNormId(raw: string): string {
  const s = String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/§/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return '';
  const lawNum = s.match(/^([A-ZÄÖÜ]{2,12})[-\s]+(\d+[A-Z]*)$/);
  if (lawNum) return `${lawNum[1]}-${lawNum[2]}`;
  const numLaw = s.match(/^(\d+[A-Z]*)\s+([A-ZÄÖÜ]{2,12})$/);
  if (numLaw) return `${numLaw[2]}-${numLaw[1]}`;
  return s.replace(/\s+/g, '-');
}

function looksLikeLawAbbrev(raw: string): boolean {
  const letters = String(raw || '').replace(/[^A-Za-zÄÖÜäöü]/g, '');
  if (letters.length < 2 || letters.length > 12) return false;
  const upper = letters.replace(/[^A-ZÄÖÜ]/g, '');
  return upper.length >= 2 && upper.length >= letters.length - 1;
}

/**
 * Headnote-Zitate: `§ 16 MRG`, `§16 Abs 8 MRG`, `Paragraph 16, MRG`, `MRG-16`.
 * Nicht nackte Zahl, nicht `16` in `1096`, nicht `Abs 3` als § 3, nicht „nach § 53“.
 */
export function extractHeadnoteNormIds(text: string): string[] {
  const src = String(text || '');
  if (!src.trim()) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (law: string, num: string) => {
    if (!looksLikeLawAbbrev(law)) return;
    const id = canonNormId(`${law}-${num}`);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };
  const paraLaw =
    /(?:§|paragraph)\s*(\d+[a-zA-Z]?)\s*(?:[,.]?\s*(?:abs(?:atz)?\.?|z(?:iffer)?\.?)\s*\d+[a-zA-Z]?\s*)*[,.]?\s*([A-Za-zÄÖÜäöü]{2,12})\b/gi;
  let m: RegExpExecArray | null;
  while ((m = paraLaw.exec(src))) add(m[2], m[1]);
  const lawNum = /\b([A-Za-zÄÖÜäöü]{2,12})-(\d+[A-Za-z]?)\b/g;
  while ((m = lawNum.exec(src))) add(m[1], m[2]);
  const lawPara = /\b([A-ZÄÖÜ]{2,12})\s*§\s*(\d+[a-zA-Z]?)\b/g;
  while ((m = lawPara.exec(src))) add(m[1], m[2]);
  return out;
}

export function headnoteMentionsNorm(text: string, normId: string): boolean {
  const want = canonNormId(normId);
  if (!want) return false;
  return extractHeadnoteNormIds(text).includes(want);
}

function caseHeadnoteText(item: JoinTopicCase): string {
  return [item.leitsatz || '', ...(item.kernsaetze || [])].join(' ');
}

export function caseHeadnoteMatchesLexIds(item: JoinTopicCase, lexIds: string[]): boolean {
  const want = new Set(lexIds.map(canonNormId).filter(Boolean));
  if (want.size === 0) return false;
  return extractHeadnoteNormIds(caseHeadnoteText(item)).some((id) => want.has(id));
}

export function lexCarryIntersectsNormRefs(lexIds: string[], normRefs: string[]): boolean {
  const carry = new Set(lexIds.map(canonNormId).filter(Boolean));
  if (carry.size === 0) return false;
  for (const ref of normRefs) {
    const id = canonNormId(ref);
    if (id && carry.has(id)) return true;
  }
  return false;
}

export function filterCasesByLexCarry<T extends { norm_refs?: string[] }>(
  cases: T[],
  lexIds: string[],
): T[] {
  if (!lexIds.length) return cases;
  return cases.filter((c) => lexCarryIntersectsNormRefs(lexIds, c.norm_refs || []));
}

const TOPIC_STOP = new Set([
  'der', 'die', 'das', 'und', 'oder', 'von', 'vom', 'im', 'in', 'zu', 'zur', 'zum',
  'des', 'dem', 'den', 'ein', 'eine', 'einer', 'eines', 'ist', 'hat', 'fuer', 'auf',
  'mit', 'nach', 'bei', 'nicht', 'dass', 'als', 'auch', 'sich', 'sowie', 'bzw',
  'abs', 'art', 'nr', 'idf', 'bgbl', 'gesetz', 'paragraph', 'paragraf', 'artikel',
  'absatz', 'ogh', 'ris', 'gericht', 'entscheidung', 'urteil', 'rechtssatz', 'leitsatz',
  'mietrecht', 'miete', 'mieter', 'vermieter', 'wohnung', 'bestand', 'sachverhalt',
  'antrag', 'klage', 'revision', 'was', 'sagt', 'dazu', 'bitte', 'keine', 'kein',
  'nur', 'nachschlagen', 'fallanalyse', 'normen', 'norm', 'fall', 'frage',
  'rechtsfrage', 'dieser', 'diesen', 'diese', 'einem', 'einen', 'ueber', 'durch',
  'ohne', 'statt', 'dessen', 'wird', 'werden', 'sind', 'kann', 'judikatur',
  'recherche',
]);

const TOPIC_STEMS = [
  'erhaltung', 'befrist', 'kuendig', 'eigenbedarf', 'mangel', 'betriebskosten',
  'kaution', 'raeumung', 'teilurteil', 'schriftform', 'brauchbar', 'unbrauchbar',
  'zinsminderung', 'zinsbefreiung', 'gebrauchsbeeintraechtigung', 'mietzins',
  'hauptmietzins', 'verlaengerung', 'endtermin', 'untergang',
];

/** Polar Fragepunkte innerhalb derselben LEX-ID (z. B. MRG-29). */
const FRAGEPUNKT_CLUSTERS: { id: string; stems: string[]; phrases: string[] }[] = [
  {
    id: 'befristung',
    stems: ['befrist', 'schriftform', 'verlaengerungsoption', 'verlaengerung', 'endtermin'],
    phrases: ['bestimmte zeit', 'unbedingten endtermin', 'verlaengerungsoption'],
  },
  {
    id: 'untergang',
    stems: ['untergang', 'baugebrechen', 'abbruchauftrag'],
    phrases: ['rechtlichen untergang', 'abs 1 z 2', 'absatz 1 ziffer 2', 'abs 1 ziffer 2'],
  },
];

export type JoinFragepunktId = 'befristung' | 'untergang';

/** Cluster nur aus der gestellten Frage — nicht aus stillem Pin/LEX-Titel. */
export function detectFragepunktClusters(text: string): JoinFragepunktId[] {
  const folded = foldTopicText(text);
  if (!folded.trim()) return [];
  const hits: JoinFragepunktId[] = [];
  for (const cluster of FRAGEPUNKT_CLUSTERS) {
    const stemHit = cluster.stems.some((stem) => folded.includes(stem));
    const phraseHit = cluster.phrases.some((phrase) => folded.includes(foldTopicText(phrase)));
    if (stemHit || phraseHit) hits.push(cluster.id as JoinFragepunktId);
  }
  return hits;
}

export type JoinTopicCase = {
  leitsatz?: string;
  kernsaetze?: string[];
  rechtssaetze?: string[];
  norm_refs?: string[];
  score?: number;
};

export type JoinTopicInput = {
  userQuestion?: string;
  lexSources?: JoinLexSource[];
};

export type JoinTopicFilterResult<T> = {
  synthesis: T[];
  moreHits: T[];
  dropped: T[];
};

function foldTopicText(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
}

function collectTopicTokens(text: string, into: Set<string>): void {
  const folded = foldTopicText(text);
  for (const raw of folded.split(/[^a-z0-9]+/)) {
    if (!raw || TOPIC_STOP.has(raw)) continue;
    if (/^\d+$/.test(raw)) {
      if (raw.length >= 2) into.add(raw);
      continue;
    }
    if (raw.length < 3) continue;
    into.add(raw);
    for (const stem of TOPIC_STEMS) {
      if (raw.startsWith(stem)) into.add(stem);
    }
  }
}

function isStrongTopicToken(token: string): boolean {
  if (/^\d{2,}$/.test(token)) return true;
  if (TOPIC_STEMS.includes(token)) return true;
  return token.length >= 6;
}

function lexLeadIds(sources?: JoinLexSource[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const src of sources || []) {
    const id = canonNormId(String(src.norm_id || ''));
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

function buildLeadTokens(input: JoinTopicInput): Set<string> {
  const lead = new Set<string>();
  collectTopicTokens(String(input.userQuestion || ''), lead);
  for (const src of input.lexSources || []) {
    collectTopicTokens(String(src.title || ''), lead);
    collectTopicTokens(String(src.norm_id || ''), lead);
  }
  return lead;
}

/**
 * Nach hartem Join: Stimme nur bei Headnote (Leitsatz/Kern) mit Gesetz+Zahl der LEX-ID.
 * Klasse JUD_JOIN_FRAGEPUNKT: hat die Frage einen Punkt (Schrift/Befristung vs. Untergang),
 * muss die Headnote denselben Punkt tragen — gleiche LEX-ID allein reicht nicht.
 * Nackte Zahl oder andere §-Zitate zählen nicht. Unsicher → Drop/Empty.
 */
export function filterCasesByJoinTopic<T extends JoinTopicCase>(
  cases: T[],
  input: JoinTopicInput,
): JoinTopicFilterResult<T> {
  const lead = buildLeadTokens(input);
  const lexIds = lexLeadIds(input.lexSources);
  const fragepunkte = detectFragepunktClusters(String(input.userQuestion || ''));
  if (!lexIds.length) {
    return { synthesis: [], moreHits: [], dropped: [...cases] };
  }
  const ranked: { item: T; rank: number; keep: boolean; idx: number }[] = [];
  cases.forEach((item, idx) => {
    let keep = caseHeadnoteMatchesLexIds(item, lexIds);
    if (keep && fragepunkte.length > 0) {
      const headClusters = detectFragepunktClusters(caseHeadnoteText(item));
      keep = fragepunkte.some((id) => headClusters.includes(id));
    }
    const right = new Set<string>();
    collectTopicTokens(caseHeadnoteText(item), right);
    let strong = 0;
    for (const token of lead) {
      if (right.has(token) && isStrongTopicToken(token)) strong += 1;
    }
    const rank = (keep ? 3 : 0) + strong + (Number(item.score) || 0) * 0.01;
    ranked.push({ item, rank, keep, idx });
  });
  const kept = ranked
    .filter((row) => row.keep)
    .sort((a, b) => b.rank - a.rank || a.idx - b.idx)
    .map((row) => row.item);
  const dropped = ranked.filter((row) => !row.keep).map((row) => row.item);
  return {
    synthesis: kept.slice(0, JOIN_SYNTHESIS_CITE_MAX),
    moreHits: kept.slice(JOIN_SYNTHESIS_CITE_MAX),
    dropped,
  };
}

export function applyJoinTopicToJudProof<T extends JoinJudProof>(
  proof: T | null | undefined,
  input: JoinTopicInput,
): T | null | undefined {
  if (!proof) return proof;
  if (proof.status !== 'ok') return proof;
  const filtered = filterCasesByJoinTopic(proof.cases || [], input);
  const cases = [...filtered.synthesis, ...filtered.moreHits];
  if (!cases.length) return { ...proof, status: 'no_match', cases: [] };
  return { ...proof, cases };
}

export type AutoOghJoinInput = {
  isPremium: boolean;
  intent?: string;
  chipAction?: string;
  userText: string;
  lexVerified: boolean;
  hasJudProof: boolean;
  schreibenDraft?: boolean;
  alreadyJoined?: boolean;
};

const PIN_PREFIX_LEFTOVER_STOP = new Set([
  'angeheftete', 'lexcore', 'kontext', 'greift', 'passiert', 'hier',
]);

/** Gleicher Kopf wie `portalParagraphRef.ATTACHED_PARAGRAPH_CONTEXT_HEAD` (kein Import: Node-strip-types). */
const ATTACHED_PARAGRAPH_CONTEXT_HEAD = 'Angeheftete Paragraphen:';

function stripAttachedParagraphPrefix(text: string): string {
  const raw = String(text || '');
  const idx = raw.indexOf(ATTACHED_PARAGRAPH_CONTEXT_HEAD);
  if (idx < 0) return raw;
  const after = raw.slice(idx + ATTACHED_PARAGRAPH_CONTEXT_HEAD.length);
  const split = after.indexOf('\n\n');
  const rest = split >= 0 ? after.slice(split + 2) : after.replace(/^[^\n]*\n?/, '');
  return `${raw.slice(0, idx)}${rest}`.replace(/^\s+/, '');
}

function hasTopicStem(folded: string): boolean {
  const tokens = new Set<string>();
  collectTopicTokens(folded, tokens);
  for (const token of tokens) {
    for (const stem of TOPIC_STEMS) {
      if (token === stem || token.startsWith(stem)) return true;
    }
  }
  return false;
}

/** Nach Pin-/Floskel-Strip: nacktes §-Zitat zählt nicht als Fallfrage. */
function hasQuestionSubstanceBeyondCite(folded: string): boolean {
  const withoutCite = folded
    .replace(/(?:§|paragraph)\s*\d+[a-z]?(?:\s*(?:abs(?:atz)?\.?\s*\d+[a-z]?)?)*\s*(abgb|mrg|weg|wgg|lfg|zpo|jn|eo|stgb|ugb)?/g, ' ')
    .replace(/\b(abgb|mrg|weg|wgg|lfg|zpo|jn|eo|stgb|ugb)-\d+[a-z]?\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const tokens = new Set<string>();
  collectTopicTokens(withoutCite, tokens);
  for (const token of tokens) {
    if (PIN_PREFIX_LEFTOVER_STOP.has(token) || TOPIC_STOP.has(token)) continue;
    if (isStrongTopicToken(token) || token.length >= 5) return true;
  }
  return false;
}

/**
 * Pin + Floskel ohne eigene Rechtsfrage: kein Auto-OGH.
 * Pin-Prefix und Floskeln („was greift/passt hier?“, „was passiert und greift hier?“)
 * zählen nicht als klare Rechtsfrage. Nacktes „§ 29“ bleibt Absage.
 */
export function hasClearLegalQuestion(text: string): boolean {
  const raw = stripAttachedParagraphPrefix(String(text || '')).replace(/\s+/g, ' ').trim();
  if (!raw) return false;
  if (isComposerParagraphLookup(raw)) return false;
  let t = foldTopicText(raw);
  t = t.replace(/rechtsfrage\s*\([^)]*\):\s*/g, ' ');
  t = t.replace(/was\s+passiert(\s+und\s+greift)?(\s+hier)?/g, ' ');
  t = t.replace(/was\s+(passt|gilt|ist|greift)\s+hier/g, ' ');
  t = t.replace(/was\s+muss\s+ich\s+beachten/g, ' ');
  t = t.replace(/was\s+ist\s+(hier\s+)?(zu\s+beachten|relevant|wichtig)/g, ' ');
  t = t.replace(/was\s+(sagt|meint)\s+der\s+ogh(\s+dazu)?/g, ' ');
  t = t.replace(/passt\s+das(\s+hier)?/g, ' ');
  t = t.replace(/[?!.,;:]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return false;
  if (hasTopicStem(t)) return true;
  return hasQuestionSubstanceBeyondCite(t);
}

/**
 * Auto-OGH nur nach Fall-Rechtsfrage mit verifiziertem [LEX:] bei Premium.
 * Nicht nach Composer-§-Nachschlagen, Schreiben, Zusammenfassen, JUD-Call selbst.
 * Nicht nach Pin + Floskel ohne eigene Rechtsfrage.
 */
export function shouldAutoOghJoin(input: AutoOghJoinInput): boolean {
  if (!input.isPremium || !input.lexVerified) return false;
  if (input.hasJudProof || input.alreadyJoined || input.schreibenDraft) return false;
  const intent = String(input.intent || '');
  if (
    intent === 'judicature_recherche' ||
    intent === 'schreiben_erstellen' ||
    intent === 'zusammenfassen'
  ) {
    return false;
  }
  const chip = String(input.chipAction || '');
  if (chip === OGH_FOLLOWUP_ACTION || chip === 'paragraph_lookup') return false;
  if (chip.startsWith('variant_')) return false;
  if (isComposerParagraphLookup(input.userText)) return false;
  if (!hasClearLegalQuestion(input.userText)) return false;
  return true;
}
